import { ChevronRight, Inbox } from "lucide-react";
import type { ReactNode } from "react";
import type { CurrentUser } from "@/features/auth/currentUserTypes";
import { formatDashboardCount } from "@/features/dashboard/metricFormatters";
import type { DashboardPeriodState } from "@/features/dashboard/period";
import type {
  CheckerDashboardAnalytics,
  CheckerDashboardOperations,
  DashboardAvailability,
  DashboardCountMetric,
  DashboardOperationalCountMetric,
  DashboardSectionStatus,
} from "@/features/dashboard/types";
import { DashboardFrame } from "@/components/dashboard/ledger/DashboardFrame";
import { DashboardLoadSituation } from "@/components/dashboard/ledger/DashboardLoadSituation";
import { DashboardMetricStrip } from "@/components/dashboard/ledger/DashboardMetricStrip";
import { DashboardOldestLoads } from "@/components/dashboard/ledger/DashboardOldestLoads";
import { DashboardQualityOutcomes } from "@/components/dashboard/ledger/DashboardQualityOutcomes";
import { DashboardQuantityBreakdown } from "@/components/dashboard/ledger/DashboardQuantityBreakdown";
import { DashboardSection } from "@/components/dashboard/ledger/DashboardSection";
import { DashboardState } from "@/components/dashboard/ledger/DashboardState";

const CHECKER_DASHBOARD_CSS = `
  .checker-dashboard {
    min-width: 0;
    color: var(--ledger-text);
  }

  .checker-dashboard__stack {
    display: grid;
    gap: 18px;
    min-width: 0;
  }

  .checker-dashboard__stack > * { min-width: 0; }

  .checker-dashboard__operations {
    align-items: stretch;
    display: grid;
    gap: 14px;
    grid-template-columns: minmax(0, 1.2fr) minmax(18rem, 0.8fr);
    min-width: 0;
  }

  .checker-dashboard__operations > * { height: 100%; }

  .checker-dashboard__lower {
    align-items: stretch;
    display: grid;
    gap: 14px;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    min-width: 0;
  }

  .checker-dashboard__performance-row:focus-visible {
    outline: 2px solid var(--ledger-accent);
    outline-offset: 2px;
  }

  .checker-dashboard__performance {
    display: grid;
    min-width: 0;
  }

  .checker-dashboard__performance-row {
    appearance: none;
    align-items: center;
    width: 100%;
    min-width: 0;
    min-height: 56px;
    padding: 0.7rem 0.2rem;
    border: 0;
    border-top: 1px solid var(--ledger-border);
    background: transparent;
    color: var(--ledger-text);
    cursor: pointer;
    display: grid;
    gap: 0.65rem;
    grid-template-columns: 1.75rem minmax(0, 1fr) auto 1.1rem;
    text-align: left;
    transition:
      background-color 140ms ease,
      color 140ms ease,
      transform 120ms ease;
  }

  .checker-dashboard__performance-row:first-child { border-top: 0; }
  .checker-dashboard__performance-row:hover {
    background: color-mix(in srgb, var(--ledger-accent) 7%, var(--ledger-surface));
  }
  .checker-dashboard__performance-row:active { transform: translateY(1px); }

  .checker-dashboard__performance-position {
    color: var(--ledger-accent);
    font-family: "DM Mono", monospace;
    font-size: 0.78rem;
    font-variant-numeric: tabular-nums;
    font-weight: 800;
    text-align: center;
  }

  .checker-dashboard__performance-label {
    min-width: 0;
    overflow: hidden;
    color: var(--ledger-text);
    font-family: "DM Sans", sans-serif;
    font-size: 0.9rem;
    font-weight: 750;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .checker-dashboard__performance-value {
    color: var(--ledger-text);
    font-family: "Barlow Condensed", "Arial Narrow", sans-serif;
    font-size: clamp(1.05rem, 2vw, 1.35rem);
    font-variant-numeric: tabular-nums;
    font-weight: 700;
    line-height: 1;
    white-space: nowrap;
  }

  .checker-dashboard__performance-value[data-length="long"] {
    font-size: clamp(0.8rem, 1.45vw, 1rem);
    letter-spacing: -0.02em;
  }

  .checker-dashboard__unavailable {
    align-items: center;
    color: var(--ledger-muted);
    display: flex;
    font-family: "DM Sans", sans-serif;
    font-size: 0.88rem;
    gap: 0.55rem;
    min-height: 7rem;
  }

  @media (max-width: 900px) {
    .checker-dashboard__operations,
    .checker-dashboard__lower {
      grid-template-columns: minmax(0, 1fr);
    }
  }

  @media (max-width: 390px) {
    .checker-dashboard__performance-row {
      gap: 0.45rem;
      grid-template-columns: 1.5rem minmax(0, 1fr) auto 1rem;
    }
  }

  @media (max-width: 320px) {
    .checker-dashboard__performance-row {
      grid-template-columns: minmax(0, 1fr) auto 1rem;
    }
    .checker-dashboard__performance-position { display: none; }
    .checker-dashboard__performance-value[data-length="long"] {
      font-size: 0.72rem;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .checker-dashboard__performance-row {
      transition-duration: 0.01ms;
      transform: none !important;
    }
  }
`;

export type CheckerDashboardNavigation = { to: "/cargas" };

export type CheckerDashboardPanelRequest =
  | { mode: "quantities" }
  | { mode: "performance" }
  | { mode: "quality" }
  | { mode: "detail"; kind: "load"; id: string; title: string };

export interface CheckerDashboardProps {
  user: CurrentUser;
  analytics: CheckerDashboardAnalytics;
  operations?: CheckerDashboardOperations;
  period: DashboardPeriodState;
  pendingPeriod?: DashboardPeriodState | null;
  updating?: boolean;
  onPeriodChange: (period: DashboardPeriodState) => void;
  onNavigate: (destination: CheckerDashboardNavigation) => void;
  onOpenPanel: (request: CheckerDashboardPanelRequest) => void;
  onRetryAnalytics: () => void;
}

function CheckerAnalyticsSection({
  status,
  title,
  onRetry,
  children,
}: {
  status?: DashboardSectionStatus;
  title: string;
  onRetry: () => void;
  children: ReactNode;
}) {
  if (!status || status.status === "available") return children;
  const failed = status.status === "error";
  return (
    <DashboardSection title={title}>
      <DashboardState
        status={failed ? "error" : "empty"}
        scope="section"
        title={failed ? "Falha ao carregar esta seção" : "Seção indisponível"}
        message={
          failed
            ? "Não foi possível carregar os dados desta seção."
            : "Dados indisponíveis para esta seção."
        }
        onRetry={failed ? onRetry : undefined}
      />
    </DashboardSection>
  );
}

const analyticsSectionStatus = (
  analytics: CheckerDashboardAnalytics,
  section: DashboardSectionStatus["section"],
) => analytics.section_status?.find((status) => status.section === section);

const asCountMetric = (
  metric: DashboardOperationalCountMetric,
): DashboardCountMetric =>
  metric.status === "available"
    ? {
        status: "available",
        unit: "count",
        value: metric.count,
        comparison: metric.comparison,
      }
    : {
        status: metric.status,
        unit: "count",
        reason_code: metric.reason_code,
      };

function currentOpenLoadsMetric(
  analytics: CheckerDashboardAnalytics,
  operations: CheckerDashboardOperations | undefined,
): DashboardCountMetric {
  if (!operations) return asCountMetric(analytics.metrics.open_loads);
  if (operations.projection_status === "not_ready") {
    return {
      status: "unavailable",
      unit: "count",
      reason_code: "projection_not_ready",
    };
  }
  const status = operations.section_status?.find(
    ({ section }) => section === "open_loads",
  );
  if (status && status.status !== "available") {
    return {
      status: status.status,
      unit: "count",
      reason_code: status.reason_code,
    };
  }
  return {
    status: "available",
    unit: "count",
    value: operations.open_loads.total,
  };
}

function oldestLoadsState(operations: CheckerDashboardOperations | undefined) {
  if (!operations || operations.projection_status === "not_ready") {
    return { status: "unavailable" as const, loads: [] };
  }
  const status = operations.section_status?.find(
    ({ section }) => section === "oldest_open",
  );
  return {
    status: (status?.status ?? "available") as DashboardAvailability,
    loads:
      status && status.status !== "available" ? [] : operations.oldest_open,
  };
}

interface PerformanceRow {
  label: string;
  count: number;
}

function CheckerPerformance({
  rows,
  onOpen,
}: {
  rows: readonly PerformanceRow[];
  onOpen: () => void;
}) {
  if (rows.length === 0) {
    return (
      <div className="checker-dashboard__unavailable" role="status">
        <Inbox aria-hidden="true" size={18} />
        Dado indisponível.
      </div>
    );
  }
  return (
    <div className="checker-dashboard__performance">
      {rows.map((row, index) => {
        const count = formatDashboardCount(row.count);
        return (
          <button
            type="button"
            className="checker-dashboard__performance-row"
            aria-label={`${row.label}: ${count.accessibleLabel}; ver meu desempenho`}
            onClick={onOpen}
            key={row.label}
          >
            <span className="checker-dashboard__performance-position">
              {index + 1}
            </span>
            <span className="checker-dashboard__performance-label">
              {row.label}
            </span>
            <strong
              className="checker-dashboard__performance-value"
              data-length={count.full.length > 12 ? "long" : "regular"}
            >
              {count.full}
            </strong>
            <ChevronRight aria-hidden="true" size={17} />
          </button>
        );
      })}
    </div>
  );
}

export function CheckerDashboard({
  user,
  analytics,
  operations,
  period,
  pendingPeriod,
  updating = false,
  onPeriodChange,
  onNavigate,
  onOpenPanel,
  onRetryAnalytics,
}: CheckerDashboardProps) {
  const openCargas = () => onNavigate({ to: "/cargas" });
  const checkedLoads = asCountMetric(analytics.metrics.checked_loads);
  const checkedItems = asCountMetric(analytics.metrics.checked_items);
  const divergences = asCountMetric(analytics.metrics.divergences);
  const quantities =
    analytics.metrics.checked_quantities.status === "available"
      ? analytics.metrics.checked_quantities.quantities
      : [];
  const personal = analytics.sections.checker_highlights.find(
    ({ checker_id }) => checker_id === user.id,
  );
  const fallbackRows: PerformanceRow[] =
    analytics.metrics.checked_items.status === "available" &&
    analytics.metrics.checked_loads.status === "available" &&
    analytics.metrics.divergences.status === "available"
      ? [
          {
            label: "Itens conferidos",
            count: analytics.metrics.checked_items.count,
          },
          {
            label: "Cargas concluídas",
            count: analytics.metrics.checked_loads.count,
          },
          {
            label: "Divergências",
            count: analytics.metrics.divergences.count,
          },
        ]
      : [];
  const performanceRows: PerformanceRow[] = personal
    ? [
        { label: "Itens conferidos", count: personal.checked_items },
        { label: "Cargas concluídas", count: personal.concluded_loads },
        { label: "Divergências", count: personal.divergences },
      ]
    : fallbackRows;
  const oldest = oldestLoadsState(operations);

  return (
    <DashboardFrame
      title="Painel de conferência"
      className="checker-dashboard"
      period={period}
      pendingPeriod={pendingPeriod}
      periodMetadata={analytics.period}
      onPeriodChange={onPeriodChange}
      updating={updating}
    >
      <style>{CHECKER_DASHBOARD_CSS}</style>
      <div className="checker-dashboard__stack" data-dashboard-role={user.role}>
        <div
          data-checker-block="headlines"
          data-testid="checker-dashboard-headlines"
        >
          <DashboardMetricStrip
            ariaLabel="Indicadores operacionais do conferente"
            items={[
              {
                id: "checked-loads",
                label: "Cargas conferidas",
                metric: checkedLoads,
                onSelect: openCargas,
              },
              {
                id: "open-loads",
                label: "Cargas abertas",
                metric: currentOpenLoadsMetric(analytics, operations),
                onSelect: openCargas,
              },
              {
                id: "checked-items",
                label: "Itens conferidos",
                metric: checkedItems,
                onSelect: () => onOpenPanel({ mode: "quantities" }),
              },
              {
                id: "divergences",
                label: "Divergências",
                metric: divergences,
                comparisonGoal: "lower-is-better",
                onSelect: () => onOpenPanel({ mode: "quality" }),
              },
            ]}
          />
        </div>

        <div
          className="checker-dashboard__operations"
          data-checker-block="oldest-and-situation"
          data-testid="checker-dashboard-operations"
          data-desktop-layout="side-by-side"
          data-mobile-layout="stacked"
        >
          <DashboardOldestLoads
            loads={oldest.loads}
            status={oldest.status}
            onViewAll={openCargas}
            onOpenLoad={(load) =>
              onOpenPanel({
                mode: "detail",
                kind: "load",
                id: load.order_id,
                title: load.label,
              })
            }
          />
          <CheckerAnalyticsSection
            status={analyticsSectionStatus(analytics, "load_situation")}
            title="Situação das cargas"
            onRetry={onRetryAnalytics}
          >
            <DashboardLoadSituation
              mode="operational"
              situation={analytics.sections.load_situation}
              onSelectState={openCargas}
            />
          </CheckerAnalyticsSection>
        </div>

        <div
          className="checker-dashboard__lower"
          data-checker-block="quantities-and-performance"
          data-testid="checker-dashboard-lower"
        >
          <DashboardSection
            title="Quantidades conferidas"
            action={
              <button
                type="button"
                className="dashboard-ledger-action"
                aria-label="Ver detalhes de quantidades conferidas"
                onClick={() => onOpenPanel({ mode: "quantities" })}
              >
                Ver detalhes
              </button>
            }
          >
            {analytics.metrics.checked_quantities.status === "available" ? (
              <DashboardQuantityBreakdown
                quantities={quantities}
                ariaLabel="Quantidades conferidas"
                onSelect={() => onOpenPanel({ mode: "quantities" })}
              />
            ) : (
              <div className="checker-dashboard__unavailable" role="status">
                <Inbox aria-hidden="true" size={18} />
                Esta seção está indisponível.
              </div>
            )}
          </DashboardSection>
          <CheckerAnalyticsSection
            status={analyticsSectionStatus(analytics, "checker_highlights")}
            title="Meu desempenho"
            onRetry={onRetryAnalytics}
          >
            <DashboardSection
              title="Meu desempenho"
              action={
                <button
                  type="button"
                  className="dashboard-ledger-action"
                  aria-label="Ver detalhes de meu desempenho"
                  onClick={() => onOpenPanel({ mode: "performance" })}
                >
                  Ver detalhes
                </button>
              }
            >
              <CheckerPerformance
                rows={performanceRows}
                onOpen={() => onOpenPanel({ mode: "performance" })}
              />
            </DashboardSection>
          </CheckerAnalyticsSection>
        </div>

        <div
          data-checker-block="divergence-outcomes"
          data-testid="checker-dashboard-outcomes"
        >
          <CheckerAnalyticsSection
            status={analyticsSectionStatus(analytics, "divergence_outcomes")}
            title="Resultado das divergências"
            onRetry={onRetryAnalytics}
          >
            <DashboardSection
              title="Resultado das divergências"
              action={
                <button
                  type="button"
                  className="dashboard-ledger-action"
                  aria-label="Ver detalhes do resultado das divergências"
                  onClick={() => onOpenPanel({ mode: "quality" })}
                >
                  Ver detalhes
                </button>
              }
            >
              <DashboardQualityOutcomes
                outcomes={analytics.sections.divergence_outcomes}
                ariaLabel="Resultado das divergências"
                onSelect={() => onOpenPanel({ mode: "quality" })}
              />
            </DashboardSection>
          </CheckerAnalyticsSection>
        </div>
      </div>
    </DashboardFrame>
  );
}
